import { useEffect, useMemo, useState } from "react";
import { TagIcon, CloudIcon, BookOpenIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import Header from "common/components/Header/Header";
import StarRating from "components/StarRating/StarRating";
import useList from "hooks/useList";
import useFetch from "hooks/useFetch";
import type { Review } from "types";
import type { Zone } from "types/Zone";

// ─── Types ────────────────────────────────────────────────────────────────────

type ReviewWithZone = Review & {
  plot_id: Review["plot_id"] & { ricePaddy: Zone | null };
};

type ZoneStats = {
  total: number;
  weather: { average: number; distribution: Record<number, number> };
  advice: { average: number; distribution: Record<number, number> };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeStats(items: ReviewWithZone[]): ZoneStats {
  const gw = items.map((r) => r.gradeWeather).filter((g): g is number => g != null);
  const ga = items.map((r) => r.gradeAdvice).filter((g): g is number => g != null);
  const avg = (arr: number[]) =>
    arr.length > 0 ? Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10 : 0;
  const dist = (arr: number[]) => {
    const d: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    arr.forEach((g) => { if (g >= 1 && g <= 5) d[g]++; });
    return d;
  };
  return {
    total: items.length,
    weather: { average: avg(gw), distribution: dist(gw) },
    advice: { average: avg(ga), distribution: dist(ga) },
  };
}

function groupByZone(items: ReviewWithZone[]) {
  const map = new Map<string, ReviewWithZone[]>();
  for (const r of items) {
    const key = r.plot_id?.ricePaddy?.name ?? "Sans rizière";
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  }
  return Array.from(map.entries())
    .map(([name, reviews]) => ({ name, reviews, stats: computeStats(reviews) }))
    .sort((a, b) => b.reviews.length - a.reviews.length);
}

// ─── Rating Summary ───────────────────────────────────────────────────────────

function RatingSummary({
  label, Icon, color, barColor, average, distribution, total,
}: {
  label: string; Icon: React.ElementType; color: string; barColor: string;
  average: number; distribution: Record<number, number>; total: number;
}) {
  const maxCount = Math.max(...[1, 2, 3, 4, 5].map((g) => distribution[g] ?? 0), 1);
  return (
    <div className="flex-1 min-w-0">
      <div className={`flex items-center gap-1.5 mb-2 text-xs font-semibold ${color}`}>
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="flex items-end gap-2 mb-2">
        <span className="text-3xl font-black text-slate-800 leading-none">
          {average > 0 ? average.toFixed(1) : "—"}
        </span>
        <div className="pb-0.5">
          <StarRating value={Math.round(average)} readonly />
          <p className="text-xs text-slate-400 mt-0.5">{total} avis</p>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        {[5, 4, 3, 2, 1].map((star) => {
          const count = distribution[star] ?? 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={star} className="flex items-center gap-1.5 text-xs text-slate-500">
              <span className="w-3 text-right shrink-0">{star}</span>
              <svg viewBox="0 0 12 12" fill="currentColor" className="w-2.5 h-2.5 text-amber-400 shrink-0">
                <path d="M6 .5l1.4 2.9 3.1.4-2.3 2.2.6 3.1L6 7.7 3.2 9.1l.6-3.1L1.5 3.8l3.1-.4z" />
              </svg>
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${barColor}`}
                  style={{ width: `${(count / maxCount) * 100}%` }}
                />
              </div>
              <span className="w-6 text-right shrink-0 text-slate-400">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Review Card ─────────────────────────────────────────────────────────────

function ReviewCard({ review }: { review: ReviewWithZone }) {
  const date = (review as any).createdAt
    ? new Date((review as any).createdAt).toLocaleDateString("fr-FR", {
        day: "2-digit", month: "short", year: "numeric",
      })
    : null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <p className="font-semibold text-slate-800 text-sm leading-tight">
            {review.plot_id?.name ?? "—"}
          </p>
          {review.plotstep_id?.step?.editLabel && (
            <p className="text-xs text-slate-400 mt-0.5">{review.plotstep_id.step.editLabel}</p>
          )}
        </div>
        {date && <span className="text-xs text-slate-400 whitespace-nowrap">{date}</span>}
      </div>
      <div className="flex flex-col gap-1.5">
        {(review.gradeWeather ?? 0) > 0 && (
          <div className="flex items-center gap-2">
            <CloudIcon className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <span className="text-xs text-slate-400 w-14 shrink-0">Météo</span>
            <StarRating value={review.gradeWeather ?? 0} readonly />
          </div>
        )}
        {(review.gradeAdvice ?? 0) > 0 && (
          <div className="flex items-center gap-2">
            <BookOpenIcon className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            <span className="text-xs text-slate-400 w-14 shrink-0">Conseils</span>
            <StarRating value={review.gradeAdvice ?? 0} readonly />
          </div>
        )}
      </div>
      {review.description && (
        <p className="mt-2 text-xs text-slate-600 border-t border-slate-100 pt-2 line-clamp-2">
          {review.description}
        </p>
      )}
    </div>
  );
}

// ─── Zone Section ─────────────────────────────────────────────────────────────

function ZoneSection({ name, reviews, stats }: { name: string; reviews: ReviewWithZone[]; stats: ZoneStats }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? reviews : reviews.slice(0, 3);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      {/* Zone header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-slate-800">{name}</h3>
          <p className="text-xs text-slate-400 mt-0.5">{reviews.length} avis</p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          {stats.weather.average > 0 && (
            <span className="flex items-center gap-1 text-emerald-600 font-semibold">
              <CloudIcon className="w-3.5 h-3.5" />
              {stats.weather.average.toFixed(1)}/5
            </span>
          )}
          {stats.advice.average > 0 && (
            <span className="flex items-center gap-1 text-amber-600 font-semibold">
              <BookOpenIcon className="w-3.5 h-3.5" />
              {stats.advice.average.toFixed(1)}/5
            </span>
          )}
        </div>
      </div>

      {/* Cards */}
      <div className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {visible.map((r) => (
            <ReviewCard key={r.id} review={r} />
          ))}
        </div>
        {reviews.length > 3 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-3 text-xs text-primary font-medium hover:underline"
          >
            {expanded ? "Réduire ↑" : `Voir les ${reviews.length - 3} autres avis ↓`}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Reviews Page ─────────────────────────────────────────────────────────────

interface GlobalStatsData {
  reviews: {
    total: number;
    weather: { average: number; distribution: Record<string, number> };
    advice: { average: number; distribution: Record<string, number> };
  };
}

function Reviews() {
  const [zoneSearch, setZoneSearch] = useState("");

  const { data: globalStatsData } = useFetch<GlobalStatsData>("app/stats");

  const { items, isFetching, setFilters } = useList<ReviewWithZone>("reviews", {
    defaultSort: "createdAt:DESC",
    populate: { plot_id: { populate: { ricePaddy: true } }, plotstep_id: { populate: "*" } },
    defaultItemsPerPage: 500,
    cachePrefix: "reviews_all",
  });

  // Filtre serveur par nom de zone (deep filter Strapi)
  useEffect(() => {
    const t = setTimeout(() => {
      if (zoneSearch.trim()) {
        setFilters({ plot_id: { ricePaddy: { name: { $containsi: zoneSearch.trim() } } } });
      } else {
        setFilters({});
      }
    }, 350);
    return () => clearTimeout(t);
  }, [zoneSearch, setFilters]);

  const reviews = items ?? [];

  const globalStats = globalStatsData?.reviews ?? null;
  const zoneGroups = useMemo(() => groupByZone(reviews), [reviews]);

  return (
    <div>
      <Header title="Avis" Icon={TagIcon} noAdd />

      <div className="p-4 flex flex-col gap-4">
        {/* Zone search */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={zoneSearch}
            onChange={(e) => setZoneSearch(e.target.value)}
            placeholder="Filtrer par rizière..."
            className="w-full sm:w-72 pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
          {zoneSearch && (
            <button
              type="button"
              onClick={() => setZoneSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
            >
              ✕
            </button>
          )}
        </div>

        {/* Loading skeleton */}
        {isFetching && reviews.length === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 h-28 animate-pulse" />
            ))}
          </div>
        )}

        {/* Global summary — toujours affiché */}
        {globalStats && (
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-slate-700">Résumé global</h2>
              <span className="text-sm text-slate-400">{globalStats.total} avis</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-6">
              <RatingSummary
                label="Infos météo"
                Icon={CloudIcon}
                color="text-emerald-600"
                barColor="bg-emerald-400"
                average={globalStats.weather.average}
                distribution={globalStats.weather.distribution}
                total={globalStats.total}
              />
              <div className="hidden sm:block w-px bg-slate-100" />
              <RatingSummary
                label="Conseils"
                Icon={BookOpenIcon}
                color="text-amber-600"
                barColor="bg-amber-400"
                average={globalStats.advice.average}
                distribution={globalStats.advice.distribution}
                total={globalStats.total}
              />
            </div>
          </div>
        )}

        {!isFetching && reviews.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400 text-sm">
            {zoneSearch ? `Aucun avis pour la rizière « ${zoneSearch} »` : "Aucun avis pour l'instant"}
          </div>
        )}

        {reviews.length > 0 && (
          <>
            {/* Zone sections */}
            {zoneGroups.map((zone) => (
              <ZoneSection
                key={zone.name}
                name={zone.name}
                reviews={zone.reviews}
                stats={zone.stats}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default Reviews;
